import React from 'react';
import CreateStandardTaskForm from './Standard/CreateStandardTaskForm';
import useAuthorizationChecker from '../../../../shared/authorization/authorizationChecker';
import CreateEmailTaskForm from './Email/CreateEmailTaskForm';
import { useProjectContext } from '../../ProjectContext';
import CreateSmsTaskForm from './Sms/CreateSmsTaskForm';
import { usePopupFormManager } from 'features/nekst-widgets';
import { ProjectPermission, TaskType, TeamPermission } from 'nekst-api';
import { ButtonSize, OptionSelectionButton, SelectableOption } from 'ui-builder';
import CreateEventTaskForm from './Event/CreateEventTaskForm';
import useFeatureToggleService, { ToggledFeature } from 'feature-toggle';
import { EventTaskConfigurationInfoPopup } from 'features/tasks-forms';
import useSettingsNavigator from '../../../../settings/settingsNavigator';

export default function CreateTaskButton() {
  const { isGranted } = useAuthorizationChecker();

  const {
    isWithinContext: isWithinProjectContext,
    data: project
  } = useProjectContext();

  const popupManager = usePopupFormManager();

  const projectId = project?.id;

  const canCreateTasks = () => {
    if (isWithinProjectContext()) {
      return isGranted(ProjectPermission.CREATE_TASKS);
    } else {
      return isGranted(TeamPermission.CREATE_PROJECT_TASKS);
    }
  };

  const featureToggleService = useFeatureToggleService();

  const settingsNavigator = useSettingsNavigator();

  if (canCreateTasks()) {

    let createEventTaskOption: SelectableOption<TaskType> | undefined = undefined;

    if (featureToggleService.isFeatureEnabled(ToggledFeature.EVENT_TASKS)) {
      if (isGranted(TeamPermission.MANAGE_EVENT_TASKS)) {
        createEventTaskOption = {
          label: 'Event Task',
          value: TaskType.EVENT,
          onSelected: () => {
            popupManager.openForm(
              <CreateEventTaskForm projectId={projectId} />,
            );
          }
        };
      } else {
        createEventTaskOption = {
          label: 'Event Task',
          value: TaskType.EVENT,
          onSelected: () => {
            popupManager.openForm(
              <EventTaskConfigurationInfoPopup
                openSettingsFunc={settingsNavigator.openCalendarIntegrationPage}
              />,
            );
          }
        };
      }
    }

    return (
      <OptionSelectionButton<TaskType>
        text="Add task"
        options={[
          {
            label: 'Standard Task',
            value: TaskType.STANDARD,
            onSelected: () => {
              popupManager.openForm(
                <CreateStandardTaskForm projectId={projectId} />,
              );
            },
          },
          {
            label: 'Email Task',
            value: TaskType.EMAIL,
            onSelected: () => {
              popupManager.openForm(
                <CreateEmailTaskForm projectId={projectId} />,
              );
            }
          },
          isGranted(TeamPermission.USE_SMS_TASKS_FEATURE) && {
            label: 'SMS Task',
            value: TaskType.SMS,
            onSelected: () => {
              popupManager.openForm(
                <CreateSmsTaskForm projectId={projectId} />,
              );
            }
          },
          ...createEventTaskOption ? [createEventTaskOption] : [],
        ]}
        size={ButtonSize.MEDIUM}
        dataKey="add-task-button"
      />
    );
  } else {
    return null;
  }
}
